/**
 * Strategic objectives as criteria (migration 1066) — the list a person ticks.
 *
 * Shared by the track's Criteria & scales tab (each tick saves at once) and the cycle
 * wizard's Criteria step (ticks join the step's plan and save with it). Each objective
 * becomes the criterion "Advances <objective>"; an item's score on it is its link to
 * the objective (None, Indirect, Secondary, Primary). A track takes at most three: with
 * three ticked, the rest cannot be ticked and the line says why.
 */
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ds';
import { MAX_OBJECTIVES_PER_TRACK } from '@/lib/objectiveCriteria';
import type { CycleObjectiveOption } from '@/hooks/prioritization/useCycleObjectives';

export interface ObjectiveCriteriaChooserProps {
  options: CycleObjectiveOption[];
  loading?: boolean;
  strategyName?: string | null;
  isOn: (key: string) => boolean;
  onToggle: (option: CycleObjectiveOption) => void;
  /** No tick can change (criteria locked, or the person may not change them). */
  disabled?: boolean;
  /** Why nothing can change, when `disabled`. */
  disabledReason?: string;
  /** The objective being saved right now. */
  busyKey?: string | null;
  /** Ticks on a track beyond these options that count toward three (objectives not offered here). */
  alsoChosenCount?: number;
}

export function ObjectiveCriteriaChooser({
  options,
  loading,
  strategyName,
  isOn,
  onToggle,
  disabled,
  disabledReason,
  busyKey,
  alsoChosenCount = 0,
}: ObjectiveCriteriaChooserProps) {
  const chosen = options.filter((o) => isOn(o.key)).length + alsoChosenCount;
  const full = chosen >= MAX_OBJECTIVES_PER_TRACK;

  return (
    <div className="space-y-2" data-testid="objective-criteria">
      <div>
        <div className="text-sm font-medium text-[var(--text-pri)]">Strategic objectives</div>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          Score items on how strongly they advance an objective{strategyName ? ` of ${strategyName}` : ''}.
          An item&apos;s score is its link to the objective — None, Indirect, Secondary or Primary — so
          scoring it here and linking it on the item are the same act. Up to {MAX_OBJECTIVES_PER_TRACK}.
        </p>
      </div>

      {loading && options.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)] flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" /> Reading objectives…
        </p>
      ) : options.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)]">
          {strategyName ? `${strategyName} has no active objectives.` : 'This workspace has no active objectives.'}{' '}
          <Link to="/prioritization/strategy/objectives" className="underline">Add an objective</Link>
        </p>
      ) : (
        <ul className="space-y-1.5">
          {options.map((o) => {
            const on = isOn(o.key);
            return (
              <li key={o.key}>
                <Checkbox
                  checked={on}
                  disabled={disabled || busyKey != null || (!on && full)}
                  onChange={() => onToggle(o)}
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      Advances {o.objective.name}
                      {busyKey === o.key && <Loader2 className="h-3 w-3 animate-spin" />}
                    </span>
                  }
                  description={o.objective.description || undefined}
                />
              </li>
            );
          })}
        </ul>
      )}

      {disabled && disabledReason ? (
        <p className="text-xs text-[var(--text-ter)]">{disabledReason}</p>
      ) : full && options.some((o) => !isOn(o.key)) ? (
        <p className="text-xs text-[var(--text-ter)]">
          {MAX_OBJECTIVES_PER_TRACK} objectives are chosen, the most a track takes. Untick one to choose another.
        </p>
      ) : null}
    </div>
  );
}
